"use client";

import { createContext, type ReactNode, useContext, useRef } from "react";

/**
 * Shared `click_id` for a single monetized unit.
 *
 * A monetized card is two cooperating components: `AffiliateImpression` (the
 * viewability wrapper) and `AffiliateLink` (the outbound link). They fire
 * separate beacons (`affiliate_impression` / `affiliate_click`). For the
 * continuous-learning loop, both must carry the SAME `click_id` so a later
 * conversion postback can be joined back to the exact impression+click pair.
 *
 * `AffiliateImpression` mints the id once and provides it here; `AffiliateLink`
 * reads it via `useClickId()`. A standalone link with no impression wrapper
 * mints + memoizes its own id, so attribution never silently drops.
 *
 * Phase 0 scope: the id is attached to PostHog + D1 events only. Mapping it onto
 * the outbound URL as a network sub-id (for the real postback join) is
 * network-specific and lands in Phase 4.
 */
const ClickIdContext = createContext<string | null>(null);

/** Mint a click id. Prefers `crypto.randomUUID`; falls back for old browsers. */
export function genClickId(): string {
	try {
		if (
			typeof crypto !== "undefined" &&
			typeof crypto.randomUUID === "function"
		) {
			return crypto.randomUUID();
		}
	} catch {
		/* fall through to the non-crypto id */
	}
	return `c_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

export function MonetizedUnitProvider({
	clickId,
	children,
}: {
	clickId: string;
	children: ReactNode;
}) {
	return (
		<ClickIdContext.Provider value={clickId}>
			{children}
		</ClickIdContext.Provider>
	);
}

/**
 * Returns a stable click id for the current monetized unit. Uses the nearest
 * `MonetizedUnitProvider`'s id when present (so an impression and its click
 * share one id); otherwise mints and memoizes a local one.
 */
export function useClickId(): string {
	const fromContext = useContext(ClickIdContext);
	const localRef = useRef<string | null>(null);
	if (fromContext) return fromContext;
	if (!localRef.current) localRef.current = genClickId();
	return localRef.current;
}
